import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  EventEmitter,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  IFormChangeEvent,
  IFormField
} from '@appgen-health-crm/api-interfaces';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  takeWhile
} from 'rxjs/operators';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';
import { ChangeData } from '../phone-input/interfaces/change-data';
import { cloneDeep, isEqual } from 'lodash';
import { ReplaySubject } from 'rxjs';
/**
 * this form component does not have submit button, only on form values change event
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'form-v1',
  templateUrl: './form-v1.component.html',
  styleUrls: ['./form-v1.component.less']
})
export class FormV1Component implements OnInit, OnDestroy {
  constructor(private imageCompress: NgxImageCompressService) {}

  active = true;
  busyMap: Record<string, boolean> = {};
  private _fields: IFormField[];
  FG!: FormGroup;

  get fields(): IFormField[] {
    return this._fields;
  }

  @Input() set fields(v: IFormField[]) {
    this.setupForm(v);
  }

  @Input() showLabels = false;
  @Input() theme: 'default' | 'crm' = 'default';
  conditionsMap: Record<string, { name: string; valid: boolean }[]> = {};

  // showValidation
  private _showValidation: boolean = false;
  @Input()
  set showValidation(val: boolean) {
    if (val) {
      for (const key in this.FG?.controls) {
        this.FG?.controls[key].markAsDirty();
        this.FG?.controls[key].markAsTouched();
        this.FG?.controls[key].updateValueAndValidity();
      }
    }
    this._showValidation = val;
  }
  get showValidation(): boolean {
    return this._showValidation;
  }

  // values
  private _values: any = null;
  @Input()
  set values(val: any) {
    if (val) {
      this.FG && !isEqual(this.FG.value, val) && this.FG.patchValue(val);
    } else {
      this.FG && this.FG.reset();
    }
    this._values = val;
  }
  get values(): any {
    return this._values;
  }

  // values
  private _readonly: boolean = false;
  @Input()
  set readOnly(val: boolean) {
    if (val) {
      this.FG && this.FG.disable();
    } else {
      this.FG && this.FG.enable();
    }
    this._readonly = val;
  }
  get readOnly(): boolean {
    return this._readonly;
  }

  private destroyFrom$: ReplaySubject<boolean>;

  @Output() onChange = new EventEmitter<IFormChangeEvent>();
  @Output() onReady = new EventEmitter();
  @Output() onBusy = new EventEmitter();

  ngOnInit(): void {}

  ngOnDestroy() {
    this.active = false;
  }

  setupForm(fields: IFormField[]) {
    if (this.destroyFrom$) {
      this.destroyFrom$.next(true);
      this.destroyFrom$.complete();
    }

    setTimeout(() => {
      this.destroyFrom$ = new ReplaySubject(1);
      this._fields = this.nestFields(fields);
      this.FG = this.createFG(this.fields || []);
      this.initFormRepeatedGroups();
      this.handleConditions(this.fields, this.FG);
      this.FG.valueChanges
        .pipe(debounceTime(250), takeUntil(this.destroyFrom$))
        .subscribe(value => {
          this.onChange.emit({ values: value, valid: this.FG.valid });
        });
      this.values && this.FG.patchValue(this.values);
      this.readOnly && this.FG.disable();
    });
  }

  dateValidator(c: AbstractControl): { [key: string]: boolean } {
    const isRequired =
      c['_rawValidators'].filter(x => x.name == 'required').length > 0;
    if (!isRequired && c.value === null) return null; // null is valid value if field is not required

    return !isNaN(Date.parse(c.value)) ? null : { dateInvalid: c.value };
  }

  phoneValidator(c: AbstractControl): { [key: string]: boolean } {
    const isRequired =
      c['_rawValidators'].filter(x => x.name == 'required').length > 0;
    if (!isRequired && c.value === null) return null; // null is valid value if field is not required

    let value: ChangeData = c.value;

    if (value) {
      const { code, iso2, num } = value;
      if (!code || !iso2 || !num || num.length != 10) {
        return { phoneInvalid: true };
      }
    }
    return null;
  }

  phone164Validator(c: AbstractControl): { [key: string]: boolean } {
    const isRequired =
      c['_rawValidators'].filter(x => x.name == 'required').length > 0;
    if (!isRequired && c.value === null) return null; // null is valid value if field is not required

    let value: string = c.value;

    if (value) {
      const phoneRegex = value.startsWith('+1')
        ? /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{5}$/
        : /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
      const isValidPhone = phoneRegex.test(value);
      if (!isValidPhone) {
        return { phoneInvalid: true };
      }
    }
    return null;
  }

  createFG(fields: IFormField[]) {
    const controls: { [key: string]: AbstractControl } = {};

    const addRepeatedGroup = (group: IFormField) => {
      const array = new FormArray([], { updateOn: 'change' });
      controls[group.name] = array;
    };

    const addField = (field: IFormField) => {
      const validators = [];
      if (field.required) {
        // for concent required true, this mean you must check the checkbox for the field to be valid
        if (field.type === 'consent') {
          validators.push(Validators.requiredTrue);
        } else {
          validators.push(Validators.required);
        }
      }

      // Make sure all date inputs are populated, (year, month, day)
      if (field.type === 'date') validators.push(this.dateValidator);

      if (field.type === 'email') validators.push(Validators.email);

      if (field.type === 'phone') validators.push(this.phoneValidator);

      if (field.type === 'phone164') validators.push(this.phone164Validator);

      // updateOn config
      let updateOn: 'change' | 'blur' = 'change';
      if (
        field.type === 'text' ||
        field.type === 'email' ||
        field.type === 'phone' ||
        field.type === 'phone164'
      ) {
        updateOn = 'blur';
      }
      const control = new FormControl(field.defaultValue, {
        validators,
        updateOn
      });
      controls[field.name] = control;
    };

    fields.forEach(field => {
      if (field.type === 'section' || field.isHidden) {
        return;
      }

      if (field.type === 'group') {
        addRepeatedGroup(field);
      } else {
        addField(field);
      }
    });

    return new FormGroup(controls, { updateOn: 'change' });
  }

  addGroupToRepeatedGroup(repeatedGroup: IFormField) {
    const array = this.FG.get(repeatedGroup.name) as FormArray;
    const group = this.createFG(repeatedGroup.fields);
    array.push(group);
  }

  removeGroupFromRepeatedGroupAtIndex(
    repeatedGroup: IFormField,
    index: number
  ) {
    const array = this.FG.get(repeatedGroup.name) as FormArray;
    array.removeAt(index);
  }

  checkCondition(fieldName: string) {
    return (
      !this.conditionsMap[fieldName] ||
      !this.conditionsMap[fieldName].some(
        x => x.valid == false || !this.checkCondition(x.name)
      )
    );
  }

  handleConditions(fields: IFormField[], FG: FormGroup) {
    fields.forEach(field => {
      if (!field?.conditions?.length) return;
      this.conditionsMap[field.name] = [];
      field.conditions.forEach(cond => {
        this.conditionsMap[field.name].push({ name: cond.name, valid: false });
        FG.get(cond.name.toString())
          ?.valueChanges.pipe(takeUntil(this.destroyFrom$))
          .subscribe(v => {
            this.conditionsMap[field.name].find(
              x => x.name === cond.name
            ).valid = v == cond.value;
          });
      });
    });
  }

  public dropped(files: NgxFileDropEntry[], controlName: string) {
    const fileEntry = files[0].fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      const reader = new FileReader();
      reader.addEventListener('load', (event: any) => {
        if (!!event.target.result && event.target.result != null) {
          this.compressFile(event.target.result.toString()).then(
            async compressed => {
              this.FG.get(controlName).patchValue({
                buffer: compressed,
                name: controlName,
                mimetype: file.type,
                originalName: file.name,
                encoding: 'base64'
              });
            }
          );
        }
      });

      reader.readAsDataURL(file);
    });
  }

  patchField(
    fieldName: string,
    { value, valid }: { value: any; valid: boolean }
  ) {
    this.FG.get(fieldName).patchValue(value);
    this.FG.get(fieldName).setErrors({ [fieldName]: !valid });
    // this will remove manual errors and recalculate validity
    valid && this.FG.get(fieldName).updateValueAndValidity();
  }

  async compressFile(original: string) {
    try {
      const size = this.imageCompress.byteCount(original) / 1024;
      if (size < 200) {
        // no need to compress; already too small
        return original;
      }
      if (!original.startsWith('data:image')) {
        console.info('no compression for non image files');
        return original;
      }
      console.info('selected file size =', size, 'Kb');

      const compressed = await this.imageCompress.compressFile(
        original,
        DOC_ORIENTATION.Up,
        50,
        80
      );
      console.info(
        'Size in bytes is now:',
        this.imageCompress.byteCount(compressed) / 1024
      );

      return compressed;
    } catch (error) {
      console.error(error);
      alert('Failed to compress file.');
    }
  }

  nestFields(fields: IFormField[]): IFormField[] {
    const _fields = cloneDeep(fields);
    const nested = [];
    _fields.forEach(field => {
      if (field.type !== 'group' && !field.group) {
        nested.push({ ...field });
        return;
      }

      if (field.type === 'group') {
        const group = { ...field, fields: [] };
        _fields.forEach(x => {
          if (x.group == group.name) group.fields.push({ ...x });
        });
        nested.push(group);
        return;
      }
    });
    return nested;
  }

  initFormRepeatedGroups() {
    const groups = this.fields.filter(x => x.type == 'group');
    groups.forEach(group => {
      if (group.repeatType === 'constant') {
        const count = +group.repeatConstant || 0;
        for (let i = 0; i < count; i++) {
          this.addGroupToRepeatedGroup(group);
        }
      }

      if (group.repeatType === 'field') {
        const fieldName = group.repeatFieldName;
        const fieldCtrl = this.FG.get(fieldName);
        const groupArrCtrl = this.FG.get(group.name) as FormArray;
        if (!fieldCtrl) return;
        fieldCtrl.valueChanges
          .pipe(takeUntil(this.destroyFrom$))
          .subscribe(v => {
            const count = +v || 0;
            let l = groupArrCtrl.controls.length;
            while (l != count) {
              if (l < count) {
                this.addGroupToRepeatedGroup(group);
                l++;
              }
              if (l > count) {
                this.removeGroupFromRepeatedGroupAtIndex(group, l - 1);
                l--;
              }
            }
          });
      }
    });
  }

  handleReady(e, fieldName: string) {
    console.log(fieldName, 'ready');
    this.busyMap[fieldName] = false;
    this.updateReadyStatus();
  }

  handleBusy(e, fieldName: string) {
    console.log(fieldName, 'busy');
    this.busyMap[fieldName] = true;
    this.updateReadyStatus();
  }

  updateReadyStatus() {
    const values = Object.values(this.busyMap);
    if (values.some(x => x)) {
      this.onBusy.emit();
    } else {
      this.onReady.emit();
    }
  }
}
